import React from 'react';
import { NoteWithDetails, NoteSearchParams, NoteSortOption, NoteSearchField } from './types';
import { useCategories } from '../category';
import {
  PagerBar,
  SearchListBar,
  SortBar,
  SortColumn,
  SortOption,
  SearchCriterion,
  SearchableField,
  ListItemSection,
  formatCategoryName,
  formatDate,
  ListItemDisplayData
} from '../../../components/common';
import { LoadingBar } from '../../../components/common/LoadingBar';
import '../../../components/common/CRMList.css';

interface NoteListProps {
  notes: NoteWithDetails[];
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  selectedNoteId?: number;
  onSelectNote: (note: NoteWithDetails) => void;
  onDeleteNote: (note: NoteWithDetails) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onCreateNew: () => void;
  onSearch?: (searchParams: Partial<NoteSearchParams>) => void;
  globalLoading?: boolean;
  loadingMessage?: string;
}

const NoteList: React.FC<NoteListProps> = ({
  notes,
  error,
  pagination,
  selectedNoteId,
  onSelectNote,
  onDeleteNote,
  onPageChange,
  onLimitChange,
  onCreateNew,
  onSearch,
  globalLoading = false,
  loadingMessage = 'Loading...'
}) => {
  const { categories } = useCategories();
  const [searchValue, setSearchValue] = React.useState('');
  const [selectedCriteria, setSelectedCriteria] = React.useState<SearchCriterion[]>([]);
  const [currentSorts, setCurrentSorts] = React.useState<SortOption[]>([
    {
      field: 'dateCreated',
      direction: 'desc',
      label: 'Date Created'
    }
  ]);

  const searchableFields: SearchableField[] = [
    { key: 'summary', label: 'Summary', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'tags', label: 'Tags', type: 'text' },
    { key: 'associations', label: 'Modules (Associated Entities)', type: 'text' }
  ];

  const sortColumns: SortColumn[] = [
    { field: 'summary', label: 'Summary' },
    { field: 'typeId', label: 'Type' },
    { field: 'priorityId', label: 'Priority' },
    { field: 'dateCreated', label: 'Date Created' },
    { field: 'dateUpdated', label: 'Date Updated' },
    { field: 'completionDate', label: 'Completion Date' },
    { field: 'dueByDate', label: 'Due By' }
  ];

  React.useEffect(() => {
    if (!onSearch) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const baseParams: Partial<NoteSearchParams> = {
        search: searchValue,
        page: 1
      };

      if (selectedCriteria.length > 0) {
        baseParams.fields = selectedCriteria.map(
          (criterion) => criterion.field as NoteSearchField
        );
      } else {
        baseParams.fields = undefined;
      }

      onSearch(baseParams);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [searchValue, selectedCriteria, onSearch]);

  const handleCriteriaChange = React.useCallback((criteria: SearchCriterion[]) => {
    setSelectedCriteria(criteria);
  }, []);

  const handleSortsChange = React.useCallback((sorts: SortOption[]) => {
    setCurrentSorts(sorts);

    if (!onSearch) {
      return;
    }

    if (sorts.length === 0) {
      onSearch({
        sorts: undefined,
        page: 1
      });
      return;
    }

    const sortParams: NoteSortOption[] = sorts.map(sort => ({
      field: sort.field as NoteSortOption['field'],
      direction: sort.direction
    }));

    onSearch({
      sorts: sortParams,
      page: 1
    });
  }, [onSearch]);
  
  // Format note priority display
  const formatPriority = (priorityId?: number): string => {
    if (!priorityId) return 'Normal';
    return formatCategoryName(priorityId, categories) || 'Unknown Priority';
  };

  // Format note associations
  const formatAssociations = (associations: any[]): string => {
    if (!associations || associations.length === 0) return 'No associations';
    
    const counts = associations.reduce((acc, assoc) => {
      acc[assoc.entityType] = (acc[assoc.entityType] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const parts = Object.entries(counts).map(([type, count]) => 
      `${count} ${type}${(count as number) > 1 ? 's' : ''}`
    );
    
    return parts.join(', ');
  };

  // Format note tags
  const formatTags = (tags: any[]): string => {
    if (!tags || tags.length === 0) return 'No tags';
    return tags.map(tag => tag.name).join(', ');
  };
  
  // Unified display data function for NoteList
  const getDisplayData = (note: NoteWithDetails): ListItemDisplayData => ({
    title: note.summary,
    category: formatCategoryName(note.typeId, categories),
    fields: [
      {
        name: 'priority',
        value: formatPriority(note.priorityId)
      },
      {
        name: 'associations',
        value: formatAssociations(note.associations)
      },
      {
        name: 'tags',
        value: formatTags(note.tags)
      },
      {
        name: 'dueByDate',
        value: note.dueByDate ? formatDate(note.dueByDate) : '—'
      },
      {
        name: 'completion',
        value: note.completionDate ? formatDate(note.completionDate) : 'Not completed'
      }
    ]
  });

  if (error) {
    return <div className="app-error-message">{error}</div>;
  }

  return (
    <>
      <div className="list">
        <div className="list-header">
          <h3>Notes ({pagination.total})</h3>
          <button
            className="app-btn app-btn-primary list-btn-add-item"
            onClick={onCreateNew}
            title="Add New Note"
          >
            +
          </button>
        </div>

        <SearchListBar
          searchableFields={searchableFields}
          searchValue={searchValue}
          selectedCriteria={selectedCriteria}
          onSearchChange={(value) => setSearchValue(value)}
          onCriteriaChange={handleCriteriaChange}
          placeholder="Search notes..."
        />

        <SortBar
          availableColumns={sortColumns}
          currentSorts={currentSorts}
          onSortsChange={handleSortsChange}
          defaultSort={{
            field: 'dateCreated',
            direction: 'desc',
            label: 'Date Created'
          }}
        />

        <ListItemSection
          items={notes}
          selectedItemId={selectedNoteId}
          onSelectItem={onSelectNote}
          onDeleteItem={onDeleteNote}
          deleteConfirmMessage={(note) => 
            `Are you sure you want to delete the note "${note.summary}"? This action cannot be undone.`
          }
          loading={false}
          loadingMessage=""
          emptyMessage="No notes found. Try adjusting your search criteria."
          getDisplayData={getDisplayData}
        />

        {/* Bottom PagerBar */}
        <PagerBar
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          itemsPerPage={pagination.limit}
          onPageChange={onPageChange}
          onItemsPerPageChange={onLimitChange}
        />
      </div>
      
      {/* Global Loading Bar */}
      <LoadingBar 
        isLoading={globalLoading} 
        message={loadingMessage}
      />
    </>
  );
};

export default NoteList;
